import api from './api';

/**
 * Workflow Engine API Service
 * Base URL: /workflow/api/
 */
const workflowService = {
  // ==================== Approval Endpoints ====================

  /**
   * Get all approvals for current user with optional filters
   * @param {Object} params - Query parameters (action, status, etc.)
   * @returns {Promise} - List of WorkflowApproval objects
   */
  getApprovals: (params = {}) =>
    api.get('/workflow/api/approvals/', { params }),

  /**
   * Get pending approvals for current user
   * @returns {Promise} - List of pending WorkflowApproval objects
   */
  getPendingApprovals: () =>
    api.get('/workflow/api/approvals/pending/'),

  /**
   * Get approval detail by ID
   * @param {number} id - Approval ID
   * @returns {Promise} - WorkflowApproval object
   */
  getApproval: (id) =>
    api.get(`/workflow/api/approvals/${id}/`),

  /**
   * Approve a workflow
   * @param {number} id - Approval ID
   * @param {string} comments - Optional approval comments
   * @returns {Promise} - { status: "approved" }
   */
  approveWorkflow: (id, comments = '') =>
    api.post(`/workflow/api/approvals/${id}/approve/`, { comments }),

  /**
   * Reject a workflow
   * @param {number} id - Approval ID
   * @param {string} comments - Rejection comments (required)
   * @returns {Promise} - { status: "rejected" }
   */
  rejectWorkflow: (id, comments) =>
    api.post(`/workflow/api/approvals/${id}/reject/`, { comments }),

  /**
   * Return workflow for revision
   * @param {number} id - Approval ID
   * @param {string} comments - Comments explaining what needs revision
   * @returns {Promise} - { status: "returned" }
   */
  returnForRevision: (id, comments) =>
    api.post(`/workflow/api/approvals/${id}/return_for_revision/`, { comments }),

  /**
   * Delegate approval to another user
   * @param {number} id - Approval ID
   * @param {number} delegateToUserId - User ID to delegate to
   * @param {string} comments - Optional delegation comments
   * @returns {Promise} - Delegation confirmation
   */
  delegateApproval: (id, delegateToUserId, comments = '') =>
    api.post(`/workflow/api/approvals/${id}/delegate/`, {
      delegate_to: delegateToUserId,
      comments
    }),

  // ==================== Workflow Instance Endpoints ====================

  /**
   * Get workflow instances with optional filters
   * @param {Object} params - Query parameters (status, workflow_code, etc.)
   * @returns {Promise} - List of WorkflowInstance objects
   */
  getWorkflowInstances: (params = {}) =>
    api.get('/workflow/api/workflow-instances/', { params }),

  /**
   * Get workflow instance detail
   * @param {number} id - Workflow instance ID
   * @returns {Promise} - WorkflowInstance object
   */
  getWorkflowInstance: (id) =>
    api.get(`/workflow/api/workflow-instances/${id}/`),

  /**
   * Get workflow timeline (step-by-step progress)
   * @param {number} id - Workflow instance ID
   * @returns {Promise} - Timeline with step statuses and approval history
   */
  getWorkflowTimeline: (id) =>
    api.get(`/workflow/api/workflow-instances/${id}/timeline/`),

  /**
   * Get workflow audit trail
   * @param {number} id - Workflow instance ID
   * @returns {Promise} - Full audit log of all actions
   */
  getWorkflowAuditTrail: (id) =>
    api.get(`/workflow/api/workflow-instances/${id}/audit_trail/`),

  /**
   * Get the source document that triggered the workflow
   * @param {number} id - Workflow instance ID
   * @returns {Promise} - Header fields, line-item tables and a deep link
   */
  getWorkflowDocument: (id) =>
    api.get(`/workflow/api/workflow-instances/${id}/document/`),

  /**
   * Cancel workflow instance
   * @param {number} id - Workflow instance ID
   * @param {string} reason - Cancellation reason
   * @returns {Promise} - Cancellation confirmation
   */
  cancelWorkflow: (id, reason) =>
    api.post(`/workflow/api/workflow-instances/${id}/cancel/`, { reason }),

  // ==================== Notification Endpoints ====================

  /**
   * Get user notifications
   * @param {Object} params - Query parameters (is_read, etc.)
   * @returns {Promise} - List of Notification objects
   */
  getNotifications: (params = {}) =>
    api.get('/workflow/api/notifications/', { params }),

  /**
   * Get unread notification count
   * @returns {Promise} - { count: number }
   */
  getUnreadNotificationCount: () =>
    api.get('/workflow/api/notifications/unread_count/'),

  /**
   * Mark notifications as read
   * @param {Array<number>} notificationIds - List of notification IDs to mark as read
   * @param {boolean} markAll - If true, mark all notifications as read
   * @returns {Promise} - Success confirmation
   */
  markNotificationsRead: (notificationIds = [], markAll = false) => {
    if (markAll) {
      return api.post('/workflow/api/notifications/mark_read/', { mark_all: true });
    }
    return api.post('/workflow/api/notifications/mark_read/', {
      notification_ids: notificationIds
    });
  },

  // ==================== Notification Preference Endpoints ====================

  getNotificationPreferences: () =>
    api.get('/workflow/api/notification-preferences/'),

  saveNotificationPreferences: (data) =>
    api.post('/workflow/api/notification-preferences/', data),

  patchNotificationPreferences: (id, data) =>
    api.patch(`/workflow/api/notification-preferences/${id}/`, data),

  // ==================== Delegation Endpoints ====================

  /**
   * Get delegations for current user
   * @param {Object} params - Query parameters
   * @returns {Promise} - List of ApproverDelegation objects
   */
  getDelegations: (params = {}) =>
    api.get('/workflow/api/delegations/', { params }),

  /**
   * Create a delegation
   * @param {Object} data - Delegation data (delegate_to, start_date, end_date, etc.)
   * @returns {Promise} - Created delegation object
   */
  createDelegation: (data) =>
    api.post('/workflow/api/delegations/', data),

  /**
   * Update a delegation
   * @param {number} id - Delegation ID
   * @param {Object} data - Updated delegation data
   * @returns {Promise} - Updated delegation object
   */
  updateDelegation: (id, data) =>
    api.put(`/workflow/api/delegations/${id}/`, data),

  /**
   * Delete a delegation
   * @param {number} id - Delegation ID
   * @returns {Promise} - Deletion confirmation
   */
  deleteDelegation: (id) =>
    api.delete(`/workflow/api/delegations/${id}/`),

  // ==================== Workflow Management Endpoints (Admin) ====================

  /**
   * Get all workflows with optional filters
   * @param {Object} params - Query parameters (status, object_type, etc.)
   * @returns {Promise} - List of Workflow objects
   */
  getWorkflows: (params = {}) =>
    api.get('/workflow/api/workflows/', { params }),

  /**
   * Get workflow detail by ID
   * @param {number} id - Workflow ID
   * @returns {Promise} - Workflow object with versions
   */
  getWorkflowDetail: (id) =>
    api.get(`/workflow/api/workflows/${id}/`),

  /**
   * Create new workflow
   * @param {Object} data - Workflow data (code, name, object_type, etc.)
   * @returns {Promise} - Created Workflow object
   */
  createWorkflow: (data) =>
    api.post('/workflow/api/workflows/', data),

  /**
   * Update workflow
   * @param {number} id - Workflow ID
   * @param {Object} data - Updated workflow data
   * @returns {Promise} - Updated Workflow object
   */
  updateWorkflow: (id, data) =>
    api.put(`/workflow/api/workflows/${id}/`, data),

  /**
   * Delete workflow
   * @param {number} id - Workflow ID
   * @returns {Promise} - Deletion confirmation
   */
  deleteWorkflow: (id) =>
    api.delete(`/workflow/api/workflows/${id}/`),

  /**
   * Publish workflow version (activates it)
   * @param {number} id - Workflow ID
   * @returns {Promise} - Published workflow version
   */
  publishWorkflow: (id) =>
    api.post(`/workflow/api/workflows/${id}/publish/`),

  /**
   * Clone workflow
   * @param {number} id - Workflow ID to clone
   * @returns {Promise} - Cloned workflow
   */
  cloneWorkflow: (id) =>
    api.post(`/workflow/api/workflows/${id}/clone/`),

  // ==================== Workflow Step Endpoints ====================

  /**
   * Get workflow steps for a version
   * @param {Object} params - Query parameters (workflow_version, etc.)
   * @returns {Promise} - List of WorkflowStep objects
   */
  getWorkflowSteps: (params = {}) =>
    api.get('/workflow/api/workflow-steps/', { params }),

  /**
   * Get workflow step detail
   * @param {number} id - Step ID
   * @returns {Promise} - WorkflowStep object
   */
  getWorkflowStep: (id) =>
    api.get(`/workflow/api/workflow-steps/${id}/`),

  /**
   * Create workflow step
   * @param {Object} data - Step data (code, name, step_type, etc.)
   * @returns {Promise} - Created WorkflowStep object
   */
  createWorkflowStep: (data) =>
    api.post('/workflow/api/workflow-steps/', data),

  /**
   * Update workflow step
   * @param {number} id - Step ID
   * @param {Object} data - Updated step data
   * @returns {Promise} - Updated WorkflowStep object
   */
  updateWorkflowStep: (id, data) =>
    api.put(`/workflow/api/workflow-steps/${id}/`, data),

  /**
   * Delete workflow step
   * @param {number} id - Step ID
   * @returns {Promise} - Deletion confirmation
   */
  deleteWorkflowStep: (id) =>
    api.delete(`/workflow/api/workflow-steps/${id}/`),

  // ==================== Workflow Rule Endpoints ====================

  /**
   * Get workflow rules for a workflow
   * @param {Object} params - Query parameters (workflow, etc.)
   * @returns {Promise} - List of WorkflowRule objects
   */
  getWorkflowRules: (params = {}) =>
    api.get('/workflow/api/workflow-rules/', { params }),

  /**
   * Get workflow rule detail
   * @param {number} id - Rule ID
   * @returns {Promise} - WorkflowRule object
   */
  getWorkflowRule: (id) =>
    api.get(`/workflow/api/workflow-rules/${id}/`),

  /**
   * Create workflow rule
   * @param {Object} data - Rule data (conditions, actions, etc.)
   * @returns {Promise} - Created WorkflowRule object
   */
  createWorkflowRule: (data) =>
    api.post('/workflow/api/workflow-rules/', data),

  /**
   * Update workflow rule
   * @param {number} id - Rule ID
   * @param {Object} data - Updated rule data
   * @returns {Promise} - Updated WorkflowRule object
   */
  updateWorkflowRule: (id, data) =>
    api.put(`/workflow/api/workflow-rules/${id}/`, data),

  /**
   * Delete workflow rule
   * @param {number} id - Rule ID
   * @returns {Promise} - Deletion confirmation
   */
  deleteWorkflowRule: (id) =>
    api.delete(`/workflow/api/workflow-rules/${id}/`),

  // ==================== User/Role Search Endpoints ====================

  /**
   * Search users for approver assignment
   * @param {string} query - Search query
   * @returns {Promise} - List of User objects
   */
  searchUsers: (query) =>
    api.get('/workflow/api/users/search/', { params: { q: query } }),

  /**
   * Get all roles for approver assignment
   * @returns {Promise} - List of Role objects
   */
  getRoles: () =>
    api.get('/workflow/api/roles/'),

  /**
   * Get all user groups
   * @returns {Promise} - List of UserGroup objects
   */
  getUserGroups: () =>
    api.get('/workflow/api/user-groups/'),

  // ==================== Report Endpoints ====================

  /**
   * Get workflow bottleneck report
   * @param {Object} params - Query parameters (date range, etc.)
   * @returns {Promise} - Bottleneck analysis
   */
  getBottleneckReport: (params = {}) =>
    api.get('/workflow/api/reports/bottleneck/', { params }),

  /**
   * Get SLA breach report
   * @param {Object} params - Query parameters (date range, etc.)
   * @returns {Promise} - SLA breach analysis
   */
  getSLABreachReport: (params = {}) =>
    api.get('/workflow/api/reports/sla_breach/', { params }),

  /**
   * Get workflow summary report
   * @param {Object} params - Query parameters (date range, etc.)
   * @returns {Promise} - Workflow summary statistics
   */
  getSummaryReport: (params = {}) =>
    api.get('/workflow/api/reports/summary/', { params }),
};

export default workflowService;
